import { Injectable, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
} from "rxjs";
import { catchError, filter, map, takeUntil, tap } from "rxjs/operators";
import { LocalSettingsService } from "~app/core/storage";
import { AuthUser } from "../core/auth-user.model";
import { SignInFailure } from "../core/auth.actions";
import { AuthService } from "../core/auth.service";
import { FirebaseAuthService } from "../firebase/firebase-auth.service";
import { OidcClientAuthService } from "../oidc-client/oidc-client-auth.service";

@Injectable()
export class CustomAuthService implements AuthService, OnDestroy {
  private userState = new BehaviorSubject<AuthUser>(null);
  userState$ = this.userState.asObservable();

  private errors = new Subject<void>();
  errors$ = this.errors.asObservable();

  private loginChanged = new BehaviorSubject<boolean>(false);
  loginChanged$ = this.loginChanged.asObservable();

  private tokenExpiring = new Subject<number>();
  tokenExpiring$ = this.tokenExpiring.asObservable();

  private destroy$ = new Subject<void>();

  constructor(
    private oAuthService: OidcClientAuthService,
    private firebaseAuth: FirebaseAuthService,
    private localSettingsService: LocalSettingsService,
    private translate: TranslateService,
    private store: Store
  ) {
    this.errors$ = merge(oAuthService.errors$, firebaseAuth.errors$);

    const oAuthUserState$ = this.oAuthService.userState$;
    const firebaseUserState$ = this.firebaseAuth.userState$;

    this.userState$ = combineLatest([oAuthUserState$, firebaseUserState$]).pipe(
      map((data) => {
        const [oAuthUser, firebaseUser] = data;
        if (!!oAuthUser && !!firebaseUser) {
          console.log("AuthUser and FireBaseUser Ok");
          return oAuthUser;
        }
        console.log("AuthUser and FireBaseUser Not Ok");
        if (!!oAuthUser && (!oAuthUser.email || oAuthUser.email.length === 0)) {
          console.log("Sign-in failed - email claim missing from B2C token");
          this.store.dispatch(new SignInFailure(
            this.translate.instant("error.auth.no_authentication_service"),
          ));
          return null;
        }
        return null;
      }),
      catchError((err) => {
        console.error("userState error: ", err);
        return of(null);
      })
    );

    const oAuthLoginChange$ = this.oAuthService.loginChanged$;
    const firebaseLoginChange$ = this.firebaseAuth.loginChanged$;

    this.loginChanged$ = combineLatest([
      oAuthLoginChange$,
      firebaseLoginChange$,
    ]).pipe(
      map((data) => {
        const [oAuth, firebase] = data;
        return oAuth && firebase;
      }),
      catchError((err) => {
        console.error("loginChanged error: ", err);
        return of(false);
      })
    );

    this.tokenExpiring$ = this.oAuthService.tokenExpiring$;

    this.oAuthService.userState$
      .pipe(
        filter((user) => !!user),
        tap((_) => this.firebaseAuth.signIn()),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.firebaseAuth.errors$
      .pipe(
        filter((errMsg) => !!errMsg),
        tap((_) => this.oAuthService.removeUser()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.userState.complete();
    this.errors.complete();

    this.loginChanged.complete();
    this.tokenExpiring.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }

  signIn(returnPath?: string) {
    this.oAuthService.signIn(returnPath);
  }

  resetPassword(returnPath?: string) {
    this.oAuthService.resetPassword(returnPath);
  }

  signOut() {
    console.log("Logout Custom Auth Service");
    this.localSettingsService.clearBrowserData();
    // Firebase will be signedOut on completeLogout from oidc-client
    this.oAuthService.signOut();
  }

  isLoggedIn(): Observable<boolean> {
    return this.oAuthService.isLoggedIn();
  }
}
